import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {Checkbox} from "@khanacademy/wonder-blocks-form";
import {color, sizing} from "@khanacademy/wonder-blocks-tokens";
import {Body, HeadingSmall} from "@khanacademy/wonder-blocks-typography";

import type {AccessibilitySettingsData} from "../types/account-settings-types.ts";

type Props = {
    disabled: boolean;
    onSettingsChange: (settings: Partial<AccessibilitySettingsData>) => void;
} & AccessibilitySettingsData;

export const AccessibilitySettingsForm: React.FC<Props> = ({
    hideVisual,
    prefersReducedMotion,
    noColorInVideos,
    disabled,
    soundOn,
    onSettingsChange,
}) => {
    return (
        <View style={styles.wrapper}>
            <View style={{gap: sizing.size_120}}>
                <HeadingSmall style={styles.header} tag="h2">
                    {t`Accessibility`}
                </HeadingSmall>
                <Body>{t`Choose options that support how you see, hear, or interact with content.`}</Body>
            </View>

            <Checkbox
                checked={hideVisual}
                id="hide-visual"
                onChange={(hideVisual: boolean) =>
                    onSettingsChange({hideVisual})
                }
                disabled={disabled}
                label={t`Hide visually-dependent content`}
            />
            <Checkbox
                label={t`Reduce motion and animations`}
                id="prefers-reduced-motion"
                checked={prefersReducedMotion}
                onChange={(prefersReducedMotion: boolean) =>
                    onSettingsChange({prefersReducedMotion})
                }
                disabled={disabled}
            />
            <Checkbox
                label={t`Remove color from videos`}
                id="no-color-in-videos"
                checked={noColorInVideos}
                onChange={(noColorInVideos: boolean) =>
                    onSettingsChange({noColorInVideos})
                }
                disabled={disabled}
            />
            <Checkbox
                label={t`Mute sound effects`}
                id="sound"
                checked={!soundOn}
                onChange={(muted: boolean) =>
                    onSettingsChange({soundOn: !muted})
                }
                disabled={disabled}
            />
        </View>
    );
};

const styles = StyleSheet.create({
    header: {
        color: color.offBlack,
    },
    wrapper: {
        gap: sizing.size_240,
    },
});

export default AccessibilitySettingsForm;
